/** Continuous restore capture — debounced snapshots on meaningful local writes (Step 8). */
import { getSession } from "../auth/authService.js";
import { captureRestorePoint, RESTORE_SCOPES } from "./restorePoints.js";
import { isJourneyStorageKey } from "../db/live.js";

const DEBOUNCE_MS = 2000;
const SKIP_KEYS = new Set([
  "dh_restore_points",
  "dh_session",
  "dh_schema_version",
  "dh_j_revision",
  "dh_accounts_revision",
  "dh_password_reset_tokens",
  "dh_email_verification_tokens",
  "dh_onboarding_state",
  "dh_lifecycle_notices",
]);

const pending = new Map();
let patched = false;
let nativeSetItem = null;

function schedule(userId, scope, actionType, label) {
  if (!userId || !scope) return;
  const key = `${userId}:${scope}:${actionType}`;
  if (pending.has(key)) clearTimeout(pending.get(key).timer);
  const timer = setTimeout(() => {
    pending.delete(key);
    const session = getSession();
    captureRestorePoint(userId, {
      label,
      actorId: session?.userId || userId,
      scope,
      source: "auto",
      actionType,
    });
  }, DEBOUNCE_MS);
  pending.set(key, { timer, userId, scope, actionType, label });
}

function resolveFromSession(session, storageKey) {
  if (!session?.userId) return null;
  const role = session.role;
  if (storageKey === "dh_buyers" && (role === "buyer" || role === "admin")) {
    return { userId: session.userId, scope: RESTORE_SCOPES.buyer, actionType: "buyer.checklist", label: "Buyer checklist edit" };
  }
  if ((storageKey === "dh_seller" || storageKey === "dh_sellers") && (role === "seller" || role === "admin")) {
    return { userId: session.userId, scope: RESTORE_SCOPES.seller, actionType: "seller.portfolio", label: "Seller portfolio edit" };
  }
  if (storageKey === "dh_agent_profiles" && (role === "agent" || role === "admin")) {
    return { userId: session.userId, scope: RESTORE_SCOPES.agent_desk, actionType: "agent.profile", label: "Agent desk edit" };
  }
  if (storageKey === "dh_broker_profiles" && (role === "broker" || role === "admin")) {
    return { userId: session.userId, scope: RESTORE_SCOPES.broker_desk, actionType: "broker.profile", label: "Broker desk edit" };
  }
  if (storageKey === "dh_account_settings") {
    return { userId: session.userId, scope: RESTORE_SCOPES.settings, actionType: "settings.save", label: "Settings change" };
  }
  if (isJourneyStorageKey(storageKey) && (role === "agent" || role === "broker" || role === "buyer" || role === "seller" || role === "admin")) {
    return { userId: session.userId, scope: RESTORE_SCOPES.messaging, actionType: "messaging.update", label: "Messaging update" };
  }
  return null;
}

function onStorageWrite(key) {
  if (!key || SKIP_KEYS.has(key)) return;
  const session = getSession();
  const resolved = resolveFromSession(session, key);
  if (!resolved) return;
  schedule(resolved.userId, resolved.scope, resolved.actionType, resolved.label);
}

function patchLocalStorage() {
  if (patched || typeof window === "undefined" || !window.localStorage) return;
  patched = true;
  nativeSetItem = localStorage.setItem.bind(localStorage);
  localStorage.setItem = function patchedSetItem(key, value) {
    nativeSetItem(key, value);
    try {
      onStorageWrite(key);
    } catch {
      /* never block writes */
    }
  };
}

export function startRestoreCapture() {
  patchLocalStorage();
}

export function stopRestoreCapture() {
  pending.forEach(({ timer }) => clearTimeout(timer));
  pending.clear();
  if (patched && nativeSetItem && typeof window !== "undefined" && window.localStorage) {
    localStorage.setItem = nativeSetItem;
    patched = false;
  }
}
